// src/SocialMediaSettings.js
import React, { useState, useEffect } from 'react';
import TitleBar from './components/ui/TitleBar';
import NotificationOverlayFrame from './components/NotificationOverlayFrame';
import useTimedUnsavedWarning from './useTimedUnsavedWarning';

import { getApiBase } from './apiBase';
import { getTenantHeaders } from './api';
const API_BASE = getApiBase();

// ── Mobile helper ──────────────────────────────────────────────────────────
const getIsMobile = () => typeof window !== 'undefined' && window.innerWidth < 1024;

const PLATFORMS = [
  { value: 'facebook', label: 'FACEBOOK', icon: '📘' },
  { value: 'instagram', label: 'INSTAGRAM', icon: '📷' },
  { value: 'twitter', label: 'TWITTER / X', icon: '🐦' },
  { value: 'linkedin', label: 'LINKEDIN', icon: '💼' },
  { value: 'youtube', label: 'YOUTUBE', icon: '📺' },
  { value: 'tiktok', label: 'TIKTOK', icon: '🎵' },
  { value: 'pinterest', label: 'PINTEREST', icon: '📌' },
  { value: 'snapchat', label: 'SNAPCHAT', icon: '👻' },
  { value: 'yelp', label: 'YELP', icon: '⭐' },
  { value: 'google_business', label: 'GOOGLE BUSINESS', icon: '🔍' },
  { value: 'other', label: 'OTHER', icon: '🌐' }
];

const compareVisibleNames = (a, b) => String(a || '').localeCompare(String(b || ''), undefined, {
  sensitivity: 'base',
  numeric: true
});

const SocialMediaSettings = ({ navigate, isPhone }) => {
  const [isMobile, setIsMobile] = useState(getIsMobile);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const [accounts, setAccounts] = useState([]);
  const [savedAccountIds, setSavedAccountIds] = useState([]);
  const { unsavedWarningVisible, showUnsavedWarning, clearUnsavedWarning } = useTimedUnsavedWarning();
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);

  const [accountForm, setAccountForm] = useState({
    platform: 'facebook',
    username: '',
    url: ''
  });

  // ── Responsive listener ────────────────────────────────────────────────────
  useEffect(() => {
    const onResize = () => setIsMobile(getIsMobile());
    window.addEventListener('resize', onResize);
    setIsMobile(getIsMobile()); // force correct value after mount
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Load settings ──────────────────────────────────────────────────────────
  const loadSettings = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/settings/social`, {
        headers: { ...getTenantHeaders() }
      });
      if (res.ok) {
        const data = await res.json();
        const mapped = Array.isArray(data) ? data.map(item => ({
          id: item.id,
          isNew: false,
          platform: item.platform,
          username: item.username || '',
          url: item.url || ''
        })) : [];
        setAccounts(mapped);
        setSavedAccountIds(mapped.map(a => String(a.id)));
        clearUnsavedWarning();
      }
    } catch (error) {
      console.error('Failed to load social media settings:', error);
    } finally {
      setLoading(false);
    }
  };

  // ── Save settings ──────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      const retainedIds = new Set(
        accounts.filter(a => !a.isNew).map(a => String(a.id))
      );
      const deletedIds = savedAccountIds.filter(id => !retainedIds.has(id));

      // Deletes run first: UNIQUE (tenant_id, platform) would reject a new row
      // reusing the platform of an account removed in this same save.
      for (const id of deletedIds) {
        const res = await fetch(`${API_BASE}/api/settings/social/${id}`, {
          method: 'DELETE',
          headers: { ...getTenantHeaders() }
        });
        if (!res.ok) throw new Error(`Save failed (HTTP ${res.status})`);
      }

      for (const account of accounts) {
        const res = await fetch(
          account.isNew
            ? `${API_BASE}/api/settings/social`
            : `${API_BASE}/api/settings/social/${account.id}`,
          {
            method: account.isNew ? 'POST' : 'PUT',
            headers: { 'Content-Type': 'application/json', ...getTenantHeaders() },
            body: JSON.stringify({
              platform: account.platform,
              url: account.url,
              username: account.username
            })
          }
        );

        if (!res.ok) throw new Error(`Save failed (HTTP ${res.status})`);

        if (account.isNew) {
          const data = await res.json();
          if (!data.id) throw new Error('Save failed: missing social media account ID');

          setAccounts(prev => prev.map(item =>
            item.id === account.id
              ? { ...item, id: data.id, isNew: false }
              : item
          ));
        }
      }

      clearUnsavedWarning();
      await loadSettings();
      window.dispatchEvent(new CustomEvent('jobflow:saved', {
        detail: { message: 'SOCIAL MEDIA SAVED!' }
      }));
    } catch (error) {
      console.error('Save error:', error);
      alert('Failed to save settings: ' + error.message);
      await loadSettings();
    } finally {
      setSaving(false);
    }
  };

  const addAccount = () => {
    const username = accountForm.username.trim();
    const url = accountForm.url.trim();

    if (!username && !url) return alert('Enter username or URL');
    if (accounts.some(a => a.platform === accountForm.platform)) return alert('THAT PLATFORM IS ALREADY ADDED');

    const newAccount = {
      id: Date.now(),
      isNew: true,
      platform: accountForm.platform,
      username,
      url
    };

    setAccounts(prev => [...prev, newAccount]);
    showUnsavedWarning();
    setAccountForm({ platform: 'facebook', username: '', url: '' });
    setShowAddAccount(false);
  };

  const startEditAccount = (account) => {
    setEditingAccount(account.id);
    setAccountForm({
      platform: account.platform,
      username: account.username || '',
      url: account.url || ''
    });
  };

  const saveEditAccount = () => {
    const username = accountForm.username.trim();
    const url = accountForm.url.trim();

    if (!username && !url) return alert('Enter username or URL');
    if (accounts.some(a => a.id !== editingAccount && a.platform === accountForm.platform)) return alert('THAT PLATFORM IS ALREADY ADDED');

    setAccounts(prev => prev.map(a =>
      a.id === editingAccount
        ? { ...a, platform: accountForm.platform, username, url }
        : a
    ));
    showUnsavedWarning();

    setEditingAccount(null);
    setAccountForm({ platform: 'facebook', username: '', url: '' });
  };

  const deleteAccount = (id) => {
    if (!window.confirm('DELETE THIS SOCIAL MEDIA ACCOUNT?')) return;
    setAccounts(prev => prev.filter(a => a.id !== id));
    showUnsavedWarning();
  };

  const getPlatformInfo = (platform) => {
    return PLATFORMS.find(p => p.value === platform) || PLATFORMS[PLATFORMS.length - 1];
  };

  const sortedAccounts = [...accounts].sort((a, b) =>
    compareVisibleNames(getPlatformInfo(a.platform).label, getPlatformInfo(b.platform).label)
  );
  const usedPlatforms = accounts.map(a => a.platform);
  const availablePlatforms = PLATFORMS
    .filter(p => !usedPlatforms.includes(p.value))
    .sort((a, b) => compareVisibleNames(a.label, b.label));
  const editingAccountPlatform = editingAccount
    ? (accounts.find(a => a.id === editingAccount)?.platform || null)
    : null;
  const editPlatformOptions = PLATFORMS
    .filter(p => !usedPlatforms.includes(p.value) || p.value === editingAccountPlatform)
    .sort((a, b) => compareVisibleNames(a.label, b.label));

  const styles = {
    page: {
      padding: isPhone ? '18px 4px' : (isMobile ? '18px' : '40px'),
      backgroundColor: '#E8E2DA',
      minHeight: '100vh'
    },
    contentWrapper: {
      maxWidth: "1200px", padding: isPhone ? '0' : "0 12px",
      margin: '0 auto'
    },
    panel: {
      backgroundColor: '#E7B888',
      border: isMobile ? '6px solid black' : '10px solid black',
      padding: isPhone ? '15px 5px' : (isMobile ? '15px' : '40px'),
      marginBottom: '18px'
    },
    label: {
      fontWeight: 900,
      textTransform: 'uppercase',
      fontSize: '13px',
      marginBottom: '6px',
      display: 'block'
    },
    input: {
      padding: '14px',
      border: '4px solid black',
      fontWeight: 900,
      width: '100%',
      fontSize: '16px',
      boxSizing: 'border-box'
    },
    select: {
      padding: '14px',
      border: '4px solid black',
      fontWeight: 900,
      width: '100%',
      fontSize: '16px',
      boxSizing: 'border-box',
      backgroundColor: 'white'
    },
    row1: {
      marginBottom: '12px'
    },
    addBtn: {
      background: 'black',
      color: '#FFA500',
      border: '4px solid #E8761D',
      padding: '12px 20px',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase',
      fontSize: '14px',
      marginBottom: '12px'
    },
    card: {
      border: '4px solid black',
      padding: '14px',
      marginBottom: '12px',
      backgroundColor: 'white'
    },
    cardHeader: {
      display: 'flex',
      flexDirection: isPhone ? 'column' : 'row',
      justifyContent: 'space-between',
      alignItems: isPhone ? 'stretch' : 'center',
      gap: '10px',
      flexWrap: 'wrap'
    },
    cardInfo: {
      flex: 1,
      minWidth: isPhone ? 0 : '200px'
    },
    cardPlatform: {
      fontWeight: 900,
      fontSize: '20px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    cardMeta: {
      fontWeight: 900,
      fontSize: '14px',
      marginTop: '4px',
      color: '#666'
    },
    cardLink: {
      color: '#2563eb',
      textDecoration: 'none',
      wordBreak: 'break-all'
    },
    cardActions: {
      display: 'flex',
      gap: '8px',
      flexWrap: isPhone ? 'wrap' : 'nowrap',
      maxWidth: isPhone ? '100%' : 'none'
    },
    editBtn: {
      background: 'white',
      color: 'black',
      border: '3px solid black',
      padding: '8px 14px',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase',
      fontSize: '13px'
    },
    deleteBtn: {
      background: '#ff4444',
      color: 'white',
      border: '3px solid black',
      padding: '8px 14px',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase',
      fontSize: '13px'
    },
    visitBtn: {
      background: 'black',
      color: 'white',
      border: '3px solid #E8761D',
      padding: '8px 14px',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase',
      fontSize: '13px'
    },
    saveBtn: {
      background: 'black',
      color: '#FFA500',
      border: '5px solid #E8761D',
      padding: '16px 24px',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase',
      fontSize: '18px',
      width: isMobile ? '100%' : 'auto'
    },
    modal: {
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.85)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 5000,
      padding: '20px'
    },
    modalContent: {
      backgroundColor: 'white',
      border: '8px solid black',
      padding: '24px',
      maxWidth: '600px',
      width: '100%',
      maxHeight: '90vh',
      overflowY: 'auto'
    },
    modalTitle: {
      fontWeight: 900,
      fontSize: '22px',
      marginBottom: '16px',
      textTransform: 'uppercase'
    },
    modalBtnRow: {
      display: 'flex',
      gap: '10px',
      marginTop: '16px'
    },
    confirmBtn: {
      flex: 1,
      background: 'black',
      color: '#FFA500',
      border: '4px solid #E8761D',
      padding: '12px',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase'
    },
    cancelBtn: {
      flex: 1,
      background: 'white',
      color: 'black',
      border: '4px solid black',
      padding: '12px',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase'
    },
    loadingBox: {
      padding: '30px',
      textAlign: 'center',
      fontWeight: 900,
      fontSize: '16px'
    }
  };

  return (
    <div style={styles.page}>
      <style>{`
        .jf-social-pink:hover {
          background-color: #FFC107 !important;
          color: #000 !important;
          border-color: #FFA500 !important;
          transition: all 0.12s ease;
        }
        .jf-social-visit:hover {
          background-color: #FFC107 !important;
          color: #000 !important;
          border-color: #FFA500 !important;
          transition: all 0.12s ease;
        }
      `}</style>
      <div style={styles.contentWrapper}>
        <TitleBar
          icon="📱"
          title="SOCIAL MEDIA"
          accent="#E8761D"
          right={
            <button
              className="jf-social-pink"
              onClick={() => navigate('/settings')}
              style={{
                background: 'black',
                color: '#FFA500',
                border: '5px solid #E8761D',
                padding: '14px',
                fontWeight: 900,
                cursor: 'pointer',
                textTransform: 'uppercase'
              }}
            >
              BACK TO SETTINGS
            </button>
          }
        />

        <div style={styles.panel}>
          {loading ? (
            <div style={styles.loadingBox}>⏳ LOADING SOCIAL MEDIA ACCOUNTS...</div>
          ) : (
            <>
              <button
                className="jf-social-pink"
                style={styles.addBtn}
                onClick={() => {
                  const first = availablePlatforms[0];
                  if (!first) return alert('ALL PLATFORMS HAVE BEEN ADDED');
                  setAccountForm({ platform: first.value, username: '', url: '' });
                  setShowAddAccount(true);
                }}
              >
                + ADD SOCIAL MEDIA ACCOUNT
              </button>

              {accounts.length === 0 ? (
                <div style={{ padding: '20px', border: '4px solid black', backgroundColor: 'white', fontWeight: 900 }}>
                  No social media accounts added yet.
                </div>
              ) : (
                sortedAccounts.map(account => {
                  const platformInfo = getPlatformInfo(account.platform);
                  return (
                    <div key={account.id} style={styles.card}>
                      <div style={styles.cardHeader}>
                        <div style={styles.cardInfo}>
                          <div style={styles.cardPlatform}>
                            <span>{platformInfo.icon}</span>
                            {platformInfo.label}
                          </div>
                          {account.username && (
                            <div style={styles.cardMeta}>@{account.username}</div>
                          )}
                        </div>
                        <div style={styles.cardActions}>
                          {account.url && (
                            <button className="jf-social-visit" style={styles.visitBtn} onClick={() => window.open(account.url, '_blank')}>
                              VISIT
                            </button>
                          )}
                          <button className="jf-social-pink" style={styles.editBtn} onClick={() => startEditAccount(account)}>
                            EDIT
                          </button>
                          <button className="jf-social-pink" style={styles.deleteBtn} onClick={() => deleteAccount(account.id)}>
                            DELETE
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}

              <div style={{ marginTop: '24px' }}>
                {unsavedWarningVisible && (
                  <NotificationOverlayFrame accentColor="#FFC107" role="status" ariaLive="polite">
                    <div style={{ fontWeight: 900, fontSize: 'clamp(18px, 4.8vw, 22px)', lineHeight: 1.2 }}>
                      SOCIAL MEDIA CHANGES NOT SAVED YET
                    </div>
                    <div style={{ fontWeight: 700, fontSize: 'clamp(15px, 4vw, 18px)', lineHeight: 1.35, marginTop: '6px' }}>
                      Select SAVE ALL CHANGES to keep your changes.
                    </div>
                  </NotificationOverlayFrame>
                )}
                <button
                  className="jf-social-pink"
                  style={styles.saveBtn}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'SAVING...' : 'SAVE ALL CHANGES'}
                </button>
              </div>
            </>
          )}
        </div>

        {showAddAccount && (
          <div style={styles.modal} onClick={() => setShowAddAccount(false)}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
              <h2 style={styles.modalTitle}>ADD SOCIAL MEDIA ACCOUNT</h2>

              <div style={styles.row1}>
                <label style={styles.label}>PLATFORM</label>
                <select
                  style={styles.select}
                  value={accountForm.platform}
                  onChange={e => setAccountForm({ ...accountForm, platform: e.target.value })}
                >
                  {availablePlatforms.map(p => (
                    <option key={p.value} value={p.value}>
                      {p.icon} {p.label}
                    </option>
                  ))}
                </select>
              </div>

              <div style={styles.row1}>
                <label style={styles.label}>USERNAME / HANDLE</label>
                <input
                  style={styles.input}
                  value={accountForm.username}
                  onChange={e => setAccountForm({ ...accountForm, username: e.target.value })}
                  placeholder="@yourbusiness"
                />
              </div>

              <div style={styles.row1}>
                <label style={styles.label}>PROFILE URL</label>
                <input
                  style={styles.input}
                  value={accountForm.url}
                  onChange={e => setAccountForm({ ...accountForm, url: e.target.value })}
                  placeholder="https://facebook.com/yourbusiness"
                />
              </div>

              <div style={styles.modalBtnRow}>
                <button className="jf-social-pink" style={styles.confirmBtn} onClick={addAccount}>ADD</button>
                <button className="jf-social-pink" style={styles.cancelBtn} onClick={() => {
                  setShowAddAccount(false);
                  setAccountForm({ platform: 'facebook', username: '', url: '' });
                }}>CANCEL</button>
              </div>
            </div>
          </div>
        )}

        {editingAccount && (
          <div style={styles.modal} onClick={() => setEditingAccount(null)}>
            <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
              <h2 style={styles.modalTitle}>EDIT SOCIAL MEDIA ACCOUNT</h2>

              <div style={styles.row1}>
                <label style={styles.label}>PLATFORM</label>
                <select
                  style={styles.select}
                  value={accountForm.platform}
                  onChange={e => setAccountForm({ ...accountForm, platform: e.target.value })}
                >
                  {editPlatformOptions.map(p => (
                    <option key={p.value} value={p.value}>
                      {p.icon} {p.label}
                    </option>
                  ))}
                </select>
              </div>

              <div style={styles.row1}>
                <label style={styles.label}>USERNAME / HANDLE</label>
                <input
                  style={styles.input}
                  value={accountForm.username}
                  onChange={e => setAccountForm({ ...accountForm, username: e.target.value })}
                />
              </div>

              <div style={styles.row1}>
                <label style={styles.label}>PROFILE URL</label>
                <input
                  style={styles.input}
                  value={accountForm.url}
                  onChange={e => setAccountForm({ ...accountForm, url: e.target.value })}
                />
              </div>

              <div style={styles.modalBtnRow}>
                <button className="jf-social-pink" style={styles.confirmBtn} onClick={saveEditAccount}>SAVE</button>
                <button className="jf-social-pink" style={styles.cancelBtn} onClick={() => {
                  setEditingAccount(null);
                  setAccountForm({ platform: 'facebook', username: '', url: '' });
                }}>CANCEL</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SocialMediaSettings;
